import { IconMoreHorizontal } from '@developerz/ui';
import { useNavigate } from '@solidjs/router';
import { createEffect, createMemo, createSignal, For, type JSX, on, onMount, Show } from 'solid-js';
import { t } from '../../i18n';
import { useCardControls } from '../../lib/card-controls-context';
import { useCardMove } from '../../lib/card-move';
import { copyText } from '../../lib/clipboard';
import type { UtilBox } from '../../lib/fleetUtilization';
import { type CardMenuItem, type CardMenuOptions, cardMenu } from '../../lib/machine-card';
import { serverText } from '../../lib/machine-labels';
import { type MachineHandlers, runCardMenuItem } from '../../lib/machine-panel';
import { moveMenuFocus } from '../../lib/menu-keys';
import type { MoveMenuOptions } from '../../lib/move-menu-items';
import { orgHref } from '../../lib/org';
import { MachineJobsAtOnce } from '../MachineJobsAtOnce';
import { Popover } from '../Popover';
import { buttonClass } from '../ui/Button';
import { MenuIcon } from './MenuIcon';

// A machine's "..." menu (#6785, prototype cmenuHTML, `:1857-1900`): the ONE menu the
// Cards view's card and the List view's row both mount. Its items are `cardMenu`'s data
// (`lib/machine-card.ts`), by role and org, and `runCardMenuItem` runs them, so every panel
// item goes through the page's one opener. A row has no card to simplify, so it passes no
// `size` and its menu drops "Simplify card".
//
// Mechanics are `<Popover/>`'s (outside click, Escape, focus back to the "..." button, the
// layer stack), and each row is the SHARED popover row (`.popover__item`, its focus ring and
// pointer; `styles/fleet-menu-rows.test.ts`); the ARIA menu's own keys are here: opening focuses the first item, up and
// down wrap, Home and End jump (`moveMenuFocus`, `lib/menu-keys.ts`), Tab closes it.
//
// "JOBS AT ONCE" (#6740) does not close the menu: it hides the body behind its picker
// (`MachineJobsAtOnce`, `variant="picker"`), focus on the stored value. Escape there comes
// BACK here, focus on the "Jobs at once" item, and the popover stays open (the picker
// prevents the key's default, which `<Popover/>`'s bubble-phase listener respects); a
// second Escape closes it as always. A save that lands closes it.
//
// A `disabled` ITEM (Send work on an offline machine, #6770) is `aria-disabled`: it stays in
// the arrow-key order and says why in its `title`, and a press neither runs it nor closes
// the menu. A detail that is copy ("Offline") arrives as `detailKey` and goes through `t()`.

export type MissionCardMenuProps = MachineHandlers & {
  readonly box: UtilBox;
  readonly canWrite: boolean;
  /** The card's size, the menu offering the other one; null on a List row. */
  readonly size: {
    readonly simplified: boolean;
    readonly onSimplify: (simplified: boolean) => void;
  } | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  /** The "Jobs at once" write's token; defaults to the session's, as `setBoxPrompt` does. */
  readonly token?: string | null;
  /** Send work (#6770): the card opens its form; a List row never shows the item, and a
   *  card with no controller shows no Send work row at all. */
  readonly onSendWork?: ((box: UtilBox) => void) | undefined;
  /** Move work and Drain machine (#6772): the card opens its panel; absent, neither row shows. */
  readonly onMoveWork?: ((box: UtilBox, drain: boolean) => void) | undefined;
};

/** The "Jobs at once" item, the row the picker's Escape hands focus back to. */
const JOBS_ITEM = '[data-menu-id="jobs_at_once"]';

/**
 * Two menus offer the same rows: same ids, words, details and state in the same groups.
 * Send work's `disabled`, `detailKey` and `title` flip with the machine's presence, so they
 * are compared too, or the memo would keep the item in its old state after the flip.
 */
const sameMenu = (
  a: readonly (readonly CardMenuItem[])[],
  b: readonly (readonly CardMenuItem[])[],
): boolean =>
  a.length === b.length &&
  a.every(
    (g, i) =>
      g.length === b[i]?.length &&
      g.every((x, j) => {
        const y = b[i]?.[j];
        return (
          y !== undefined &&
          x.id === y.id &&
          x.label === y.label &&
          x.detail === y.detail &&
          x.detailKey === y.detailKey &&
          x.noteKey === y.noteKey &&
          x.title === y.title &&
          x.disabled === y.disabled
        );
      }),
  );

/**
 * The menu's groups for one card: `cardMenu`'s data, less the Send work row when the
 * card has no controller to run it (#6770) — the page hands that to a settled
 * admin/owner whose credential holds both write scopes. No controller, no row: a card
 * never offers a control whose handler is absent. The id is `send` (`cardMenu`'s
 * `sendItem`), so a group emptied here goes with the empty groups.
 */
const menuGroups = (
  props: MissionCardMenuProps,
  jobs: Exclude<CardMenuOptions['jobs'], undefined>,
  move: MoveMenuOptions | null,
): readonly (readonly CardMenuItem[])[] =>
  cardMenu(props.box, {
    canWrite: props.canWrite,
    variant: props.size === null ? 'row' : 'card',
    simplified: props.size?.simplified ?? false,
    jobs,
    move,
  })
    .map((group) => group.filter((item) => item.id !== 'send' || props.onSendWork !== undefined))
    .filter((group) => group.length > 0);

/**
 * Move work's menu options (#6772): only on a card with a move handle the person may use (an
 * admin on an own-org machine, `useCardMove`) and an opener; the org's own machines on the
 * page; and the page's pre-send check on this machine, as a key (the menu's titles take none).
 */
function moveOptions(props: MissionCardMenuProps): () => MoveMenuOptions | null {
  const m = useCardMove();
  return () => {
    if (m === null || !m.on() || props.onMoveWork === undefined || props.size === null) return null;
    const blocked = m.page.blockedWhy(props.box.id);
    return {
      machines: m.page.ownMachines(),
      blocked: blocked === null ? null : m.page.locked() > 0 ? 'locked' : 'limit',
    };
  };
}

export function MissionCardMenu(props: MissionCardMenuProps): JSX.Element {
  const navigate = useNavigate();
  // The card's job controls (#6753); a List row and a reader have none, so offer neither item.
  const controls = useCardControls();
  const jobs = () => {
    if (props.size === null || controls?.on() !== true || !controls.card.allShown()) return null;
    return {
      value: controls.card.allValue(),
      count: controls.card.allCount(),
      locked: controls.card.locked() > 0,
    };
  };
  // KEPT ACROSS A POLL: every 20-second read hands a new `box`, and new item objects would
  // rebuild every row under an open menu and drop the keyboard's focus on `body`.
  const move = moveOptions(props);
  const groups = createMemo(() => menuGroups(props, jobs(), move()), undefined, {
    equals: sameMenu,
  });
  return (
    <Popover
      label={t('fleet.machines.card_more', { name: props.box.display_name })}
      trigger={<IconMoreHorizontal />}
      triggerTitle={t('fleet.machines.card_more_title')}
      haspopup="menu"
      class="machine-menu-anchor"
      triggerClass={buttonClass({ variant: 'ghost', size: 'icon-sm' })}
      panelClass="machine-menu"
    >
      {(closePopover) => {
        let menu: HTMLDivElement | undefined;
        // The picker is up: the main body is hidden behind `MachineJobsAtOnce`'s radios.
        // Made HERE, per open: `<Popover/>` mounts this render fresh on every open and can
        // close it without us (an outside click, a second press of the trigger), so a
        // picker left up by one open never carries into the next.
        const [picking, setPicking] = createSignal(false);
        const run = (item: CardMenuItem): void =>
          runCardMenuItem(item.id, props.box, {
            onManage: props.onManage,
            onSendWork: (box) => props.onSendWork?.(box),
            onMoveWork: (box, drain) => props.onMoveWork?.(box, drain),
            onRename: props.onRename,
            onRemove: props.onRemove,
            onDrain: props.onDrain,
            onUndrain: props.onUndrain,
            onSimplify: (simplified) => props.size?.onSimplify(simplified),
            onAgents: () => navigate(orgHref('/fleet/agents')),
            onCopyId: (box) => {
              void copyText(box.id).then((ok) =>
                props.notify(
                  t(ok ? 'fleet.machines.card_copied' : 'fleet.machines.card_copy_failed'),
                  ok ? 'success' : 'error',
                ),
              );
            },
            onJobsAtOnce: () => setPicking(true),
            onJobsAll: () => controls?.card.toggleAll(),
          });
        const close = (): void => {
          setPicking(false);
          closePopover();
        };
        onMount(() => menu?.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
        // A poll that CHANGES the rows (a drain landed: Stop became Start) rebuilds them, and
        // the focused one goes with it. Focus then lands on the "..." button, never on body,
        // so the keyboard is not stranded and Escape still closes the menu.
        createEffect(
          on(
            groups,
            () => {
              const active = document.activeElement;
              if (active !== null && active !== document.body) return;
              menu?.closest('.popover')?.querySelector<HTMLElement>('.popover__trigger')?.focus();
            },
            { defer: true },
          ),
        );
        return (
          <>
            {/* Hidden, never unmounted, while the picker is up: the clicked "Jobs at once"
                row stays in the panel, so `<Popover/>`'s outside-click check still finds it
                inside, and Escape hands focus straight back to it. */}
            <div
              ref={menu}
              hidden={picking()}
              role="menu"
              aria-label={t('fleet.machines.card_more', { name: props.box.display_name })}
              onKeyDown={(e) => {
                if (e.key === 'Tab') close();
                else if (menu && moveMenuFocus(menu, e.key)) e.preventDefault();
              }}
            >
              <div class="machine-menu__head" aria-hidden="true">
                <b>{props.box.display_name}</b>
                <Show when={serverText(props.box)}>{(server) => <span>{server()}</span>}</Show>
              </div>
              <For each={groups()}>
                {(group) => (
                  <>
                    <hr class="machine-menu__sep" />
                    <For each={group}>
                      {(entry) => (
                        <button
                          type="button"
                          role="menuitem"
                          data-menu-id={entry.id}
                          class="popover__item"
                          data-danger={entry.danger === true ? '' : undefined}
                          aria-disabled={entry.disabled === true ? 'true' : undefined}
                          title={t(entry.title)}
                          onClick={() => {
                            if (entry.disabled === true) return;
                            // "Jobs at once" swaps the body for its picker; every other item
                            // closes the menu first.
                            if (entry.id !== 'jobs_at_once') close();
                            run(entry);
                          }}
                        >
                          <span class="machine-menu__icon">
                            <MenuIcon id={entry.id} />
                          </span>
                          <span class="machine-menu__label">
                            {t(entry.label)}
                            <Show when={entry.noteKey}>
                              {(note) => <small class="machine-menu__note">{t(note())}</small>}
                            </Show>
                          </span>
                          <Show when={entry.detail}>
                            {(detail) => (
                              <span class="machine-menu__detail" aria-hidden="true">
                                {detail()}
                              </span>
                            )}
                          </Show>
                          <Show when={entry.detailKey}>
                            {(key) => (
                              <span class="machine-menu__detail" aria-hidden="true">
                                {t(key())}
                              </span>
                            )}
                          </Show>
                        </button>
                      )}
                    </For>
                  </>
                )}
              </For>
            </div>
            <Show when={picking()}>
              <MachineJobsAtOnce
                box={props.box}
                canWrite={props.canWrite}
                variant="picker"
                token={props.token}
                onSaved={close}
                onClose={close}
                onBack={() => {
                  setPicking(false);
                  menu?.querySelector<HTMLElement>(JOBS_ITEM)?.focus();
                }}
              />
            </Show>
          </>
        );
      }}
    </Popover>
  );
}
